import {
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  addMonths,
  addQuarters,
  addYears,
  format,
  parseISO,
  getQuarter,
} from "date-fns";
import { nb } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SrIcon } from "@/components/ui/SrIcon";
import type { ReportPeriod } from "@/lib/reports/types";

interface PeriodPickerProps {
  value: ReportPeriod;
  onChange: (period: ReportPeriod) => void;
}

type Mode = ReportPeriod["mode"];

const MODES: { mode: Mode; label: string }[] = [
  { mode: "maaned", label: "Måned" },
  { mode: "kvartal", label: "Kvartal" },
  { mode: "aar", label: "År" },
  { mode: "egendefinert", label: "Egendefinert" },
];

const ISO = "yyyy-MM-dd";

/** Computes from/to for the given mode relative to the current `from` date. */
function periodForMode(mode: Mode, current: ReportPeriod): ReportPeriod {
  if (mode === "egendefinert") {
    return { mode, from: current.from, to: current.to };
  }
  const anchor = parseISO(current.from);
  const range =
    mode === "maaned"
      ? { from: startOfMonth(anchor), to: endOfMonth(anchor) }
      : mode === "kvartal"
        ? { from: startOfQuarter(anchor), to: endOfQuarter(anchor) }
        : { from: startOfYear(anchor), to: endOfYear(anchor) };
  return { mode, from: format(range.from, ISO), to: format(range.to, ISO) };
}

/** Steps the period by `delta` units of its mode (no-op for egendefinert). */
function stepPeriod(period: ReportPeriod, delta: number): ReportPeriod {
  if (period.mode === "egendefinert") return period;
  const anchor = parseISO(period.from);
  const shifted =
    period.mode === "maaned"
      ? addMonths(anchor, delta)
      : period.mode === "kvartal"
        ? addQuarters(anchor, delta)
        : addYears(anchor, delta);
  return periodForMode(period.mode, {
    ...period,
    from: format(shifted, ISO),
  });
}

/** Norwegian label for the current period, depending on mode. */
function periodLabel(period: ReportPeriod): string {
  const from = parseISO(period.from);
  switch (period.mode) {
    case "maaned": {
      const raw = format(from, "LLLL yyyy", { locale: nb });
      return raw.charAt(0).toUpperCase() + raw.slice(1);
    }
    case "kvartal":
      return `Q${getQuarter(from)} ${format(from, "yyyy")}`;
    case "aar":
      return format(from, "yyyy");
    case "egendefinert":
      return `${format(from, "dd.MM.yyyy")} – ${format(parseISO(period.to), "dd.MM.yyyy")}`;
  }
}

export function PeriodPicker({ value, onChange }: PeriodPickerProps) {
  const isCustom = value.mode === "egendefinert";

  /** Emits a custom-range change, clamping so from never exceeds to. */
  const emitCustom = (from: string, to: string) => {
    if (from > to) {
      // Keep the just-edited bound authoritative; collapse the other onto it.
      onChange({ mode: "egendefinert", from, to: from });
    } else {
      onChange({ mode: "egendefinert", from, to });
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex flex-wrap gap-1.5">
        {MODES.map(({ mode, label }) => (
          <Button
            key={mode}
            size="sm"
            variant={value.mode === mode ? "default" : "outline"}
            className="text-sm font-medium"
            onClick={() => onChange(periodForMode(mode, value))}
          >
            {label}
          </Button>
        ))}
      </div>

      {isCustom ? (
        <div className="flex items-center gap-2 text-sm">
          <label className="flex items-center gap-1.5">
            <span className="text-muted-foreground">Fra</span>
            <Input
              type="date"
              aria-label="Fra-dato"
              value={value.from}
              className="h-8 w-[150px]"
              onChange={(e) => emitCustom(e.target.value, value.to)}
            />
          </label>
          <label className="flex items-center gap-1.5">
            <span className="text-muted-foreground">Til</span>
            <Input
              type="date"
              aria-label="Til-dato"
              value={value.to}
              className="h-8 w-[150px]"
              onChange={(e) => emitCustom(value.from, e.target.value)}
            />
          </label>
        </div>
      ) : (
        <div className="flex items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            aria-label="Forrige periode"
            onClick={() => onChange(stepPeriod(value, -1))}
          >
            <SrIcon name="chevron-left" />
          </Button>
          <span className="min-w-[120px] text-center text-sm font-medium">
            {periodLabel(value)}
          </span>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            aria-label="Neste periode"
            onClick={() => onChange(stepPeriod(value, 1))}
          >
            <SrIcon name="chevron-right" />
          </Button>
        </div>
      )}
    </div>
  );
}
